<template>
  <v-col class="px-1 px-md-5">
    <h2>{{ $translate('ACCOUNT_SETTINGS') }}</h2>

    <v-slide-y-transition>
      <ErrorCard
        v-if="error"
        :errorMessage="errorMessage"
        :functionName="errorToRecallFunction"
        @updateUser="updateUser"
      />
    </v-slide-y-transition>

    <v-form ref="userForm">
      <v-col class="px-0">
        <v-card color="grey lighten-3" flat class="px-2 pt-3 mb-4">
          <!-- Professional user config -->
          <section>
            <v-row class="px-2 px-md-0">
              <v-checkbox v-model="user.professionalUser" class="px-1 px-md-5">
                <template v-slot:label>
                  <h3 class="text--primary">
                    {{ $translate('PROF_USER_CHECKBOX_LABEL') }}
                  </h3>
                </template>
              </v-checkbox>
            </v-row>

            <v-expand-transition>
              <section v-if="user.professionalUser">
                <v-row class="px-2 px-md-0">
                  <v-text-field
                    :label="$translate('COMPANY_NAME')"
                    v-model="user.companyName"
                    filled
                    background-color="white"
                    class="px-1 px-md-5"
                    counter
                    maxlength="255"
                    :rules="requiredRules"
                  ></v-text-field>
                </v-row>
                <v-row class="px-2 px-md-0">
                  <v-text-field
                    :label="$translate('KVK_NUMBER')"
                    v-model="user.kvkNumber"
                    filled
                    background-color="white"
                    class="px-1 px-md-5"
                    :rules="requiredRules"
                  ></v-text-field>
                  <v-text-field
                    :label="$translate('VAT_NUMBER')"
                    v-model="user.vatNumber"
                    filled
                    background-color="white"
                    class="px-1 px-md-5"
                    :rules="requiredRules"
                  ></v-text-field>
                </v-row>
              </section>
            </v-expand-transition>
          </section>

          <v-row class="px-2 px-md-0 mt-0">
            <v-text-field
              :label="$translate('FIRST_NAME')"
              v-model="user.firstName"
              filled
              background-color="white"
              class="px-1 px-md-5"
            ></v-text-field>
            <v-text-field
              :label="$translate('LAST_NAME')"
              v-model="user.lastName"
              filled
              background-color="white"
              class="px-1 px-md-5"
            ></v-text-field>
          </v-row>
          <v-row class="px-2 px-md-0">
            <v-text-field
              :label="$translate('STREET')"
              v-model="user.street"
              class="px-1 px-md-5"
              filled
              background-color="white"
              @change="geocodeAddress"
            ></v-text-field>
            <v-text-field
              :label="$translate('HOUSE_NR')"
              v-model="user.houseNo"
              class="px-1 px-md-5"
              filled
              background-color="white"
              @change="geocodeAddress"
            ></v-text-field>
          </v-row>
          <v-row class="px-2 px-md-0">
            <v-text-field
              :label="$translate('CITY')"
              v-model="user.city"
              class="px-1 px-md-5"
              filled
              background-color="white"
              @change="geocodeAddress"
            ></v-text-field>
            <v-text-field
              ref="postalcode"
              :label="$translate('POSTAL_CODE')"
              v-model="user.postalCode"
              class="px-1 px-md-5"
              filled
              background-color="white"
              @change="geocodeAddress"
            ></v-text-field>
          </v-row>
          <v-row class="px-2 px-md-0">
            <v-col class="px-0" cols="12">
              <v-select
                v-model="user.countryCode"
                :label="$translate('COUNTRY')"
                :items="getCountryCodes"
                class="px-1 px-md-5"
                filled
                background-color="white"
              >
                <template slot="item" slot-scope="data">
                  {{ getCountryName(data.item, { select: 'official' }) }}
                </template>
                <template slot="selection" slot-scope="data">
                  {{ getCountryName(data.item, { select: 'official' }) }}
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row class="px-2 px-md-0">
            <v-col class="pa-0 px-0" cols="12" md="6">
              <v-select
                v-model="selectedCountryCode"
                :items="countryCallingCodes"
                :label="$translate('COUNTRY_CODE')"
                item-text="name"
                item-value="callingCode"
                filled
                :rules="phoneNumberRules"
                background-color="white"
                class="px-1 px-md-5"
              >
              </v-select>
            </v-col>
            <v-col class="pa-0 px-0" cols="12" md="6">
              <v-text-field
                ref="phoneNumber"
                :label="$translate('PHONE_NR')"
                v-model="nationalPhoneNo"
                :rules="phoneNumberRules"
                filled
                @input="clearPhoneError"
                background-color="white"
                class="px-1 px-md-5"
               :error-messages="phoneErrorMessages"
              ></v-text-field>
            </v-col>
          </v-row>
          <!--whatsapp-->
          <v-row class="px-2 pb-3 px-md-0">
            <v-col class="pa-0 px-md-5 pb-2" cols="12" md="6">
              <v-btn
                block
                color="primary"
                class="px-1 px-md-5 primary-font-color"
                @click="requestWhatsappOptIn"
                :disabled="whatsappAllowed"
              >
                {{ $translate('RECEIVE_WHATSAPP') }}
              </v-btn>
              <p v-if="whatsappAllowed">
                {{ $translate('OPT_OUT_MSG')
                }}<a href="#" @click="whatsappOptOut">{{
                  $translate('CLICK_HERE')
                }}</a>
              </p>
            </v-col>
            <!-- sms -->
            <v-col class="pa-0 px-md-5 pb-2" cols="12" md="6">
              <v-btn
                block
                color="primary"
                class="px-1 px-md-5 primary-font-color"
                @click="requestSMSOptIn"
                :disabled="SMSAllowed"
              >
                {{ $translate('RECEIVE_SMS') }}
              </v-btn>

              <p v-if="SMSAllowed">
                {{ $translate('OPT_OUT_MSG_SMS')
                }}<a href="#" @click="SMSOptOut">{{
                  $translate('CLICK_HERE')
                }}</a>
              </p>
            </v-col>
          </v-row>
          <v-row class="ma-0 pb-0">
            <v-col>
              <v-subheader style="font-size: large" class="pl-0">{{
                $translate('SHOPPINGLIST_REMINDER_PREFERENCE')
              }}</v-subheader>
            </v-col>
          </v-row>
          <v-row class="ma-0 pb-0" no-gutters>
            <v-col
              v-for="(communicationChannel, index) in communicationChannels"
              :key="index"
              cols="12"
              lg="6"
              class="py-0"
            >
              <v-checkbox
                @click.native.prevent.stop.capture="
                  changeCommunicationPreference(
                    communicationChannel.CommunicationChannel,
                    communicationChannel
                  )
                "
                v-model="communicationChannel.Enabled"
                :label="communicationChannel.CommunicationChannel"
                :disabled="!hasApp(communicationChannel.CommunicationChannel)"
                class="mt-0 pl-4"
                style="margin-bottom: -15px"
              />
              <h6
                v-if="!hasApp(communicationChannel.CommunicationChannel)"
                class="pl-4"
                style="color: rgba(0, 0, 0, 0.6); margin-bottom: 0"
              >
                {{ $translate('USER_DOES_NOT_HAVE_PARTNERGROUP_APP') }}
              </h6>
            </v-col>
          </v-row>

          <v-row class="px-0">
            <v-col>
              <v-subheader style="font-size: medium" class="ml-0"
                >{{ $translate('DATE_OF_BIRTH') }}:</v-subheader
              >
              <v-row class="ml-1">
                <v-col sm="2">
                  <v-select
                    v-model="selectedYear"
                    :items="years"
                    :label="$translate('YEAR')"
                    class="px-0 px-md-0"
                    filled
                    background-color="white"
                    @input="formatDate"
                  ></v-select>
                </v-col>
                <v-col sm="3">
                  <v-select
                    v-model="selectedMonth"
                    :items="monthOptions"
                    :label="$translate('MONTH')"
                    class="px-0 px-md-0"
                    filled
                    background-color="white"
                    @input="formatDate"
                  ></v-select>
                </v-col>
                <v-col sm="2">
                  <v-select
                    v-model="selectedDay"
                    :items="getDaysInMonth(selectedYear, selectedMonth)"
                    :label="$translate('DAY')"
                    class="px-0 px-md-0"
                    filled
                    background-color="white"
                    @input="formatDate"
                  ></v-select>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row class="px-2 px-md-0">
            <v-file-input
              :rules="rules"
              accept="image/png, image/jpeg, image/bmp"
              :placeholder="$translate('PICK_AN_AVATAR')"
              prepend-icon="camera_alt"
              label="Avatar"
              filled
              background-color="white"
              class="px-1 px-md-5"
              v-model="profilePicture"
            />
          </v-row>
          <v-row class="px-3 px-md-5 py-3 d-inline-flex">
            <!-- Change email button -->
            <v-col class="pa-0 pb-3">
              <v-btn
                color="primary"
                class="primary-font-color"
                @click="onChangeEmailBtnClick"
              >
                <v-icon left>lock</v-icon>
                {{ $translate('RESET_EMAIL_TITLE') }}
              </v-btn>
            </v-col>
            <!-- Change password button -->
            <v-col class="pa-0 pb-3 pl-sm-4">
              <v-btn
                color="primary"
                class="primary-font-color"
                @click="onChangePasswordBtnClick"
              >
                <v-icon left>lock</v-icon>
                {{ $translate('RESET_PASS_TITLE') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- Direct debit -->
        <v-card v-if="!DirectDebitExcluded" flat color="grey lighten-3" class="px-2 py-3 mt-6">
          <UserSettingsAccountDirectDebit
            :user="user"
          ></UserSettingsAccountDirectDebit>
        </v-card>

        <v-row class="mt-5 mx-0">
          <v-btn
            color="red"
            dark
            @click="deleteAccountDialog = true"
            depressed
            class="mb-2"
          >
            <v-icon left>mdi-delete</v-icon>
            {{ $translate('DELETE_ACCOUNT') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            @click="updateUser"
            depressed
            class="mb-2 primary-font-color"
            :loading="updateUserLoading"
          >
            <v-icon left>mdi-content-save</v-icon>
            {{ $translate('SAVE') }}
          </v-btn>
        </v-row>
      </v-col>
    </v-form>

    <!-- Dialog to change email -->
    <v-dialog
      v-model="changeEmailDialogActive"
      max-width="400px"
      class="rounded-lg"
      transition="scroll-y-reverse-transition"
      persistent
    >
      <v-card v-if="changeEmailDialogActive" class="pa-0">
        <ChangeEmail @close="changeEmailDialogActive = false" />
      </v-card>
    </v-dialog>

    <!-- Dialog to change password -->
    <v-dialog
      v-model="changePasswordDialogActive"
      max-width="400px"
      class="rounded-lg"
      transition="scroll-y-reverse-transition"
      persistent
    >
      <v-card v-if="changePasswordDialogActive" class="pa-0">
        <ChangePassword @close="changePasswordDialogActive = false" />
      </v-card>
    </v-dialog>

    <!-- confirm dialog for account deletion-->
    <ConfirmDialog
      :visible="deleteAccountDialog"
      :title="confirmDialogTitle"
      :message="confirmDialogMsg"
      :positiveBtnText="confirmDialogPositiveBtnText"
      :negativeBtnText="confirmDialogNegativeBtnText"
      @positiveBtnClicked="deleteUser()"
      @negativeBtnClicked="deleteAccountDialog = false"
    ></ConfirmDialog>
  </v-col>
</template>

<script>
import doRequest from '@/api/dssRequest';
import forwardGeocoding from '@/api/Geocoding';
import {
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { countries } from 'countries-list';
import { mapGetters } from 'vuex';
import utility from '@/shared/utility';
import UserObject from '@/objects/UserObject';
import { requiredRules } from '@/shared/validations_rules';
import ErrorCard from '@/components/generalComponents/ErrorHandling/ErrorCard';
import ChangePassword from '@/components/account/AccountChangePassword';
import ChangeEmail from '@/components/account/AccountChangeEmail';
import ConfirmDialog from '@/components/generalComponents/uiComponents/ConfirmDialog';
import UserSettingsAccountDirectDebit from '@/components/userSettings/UserSettings_Account_DirectDebit';
import { cloneDeep } from 'lodash';

// import countries from 'i18n-iso-countries';
let countriesCode = require('i18n-iso-countries');
countriesCode.registerLocale(require('i18n-iso-countries/langs/en.json'));
countriesCode.registerLocale(require('i18n-iso-countries/langs/nl.json'));
countriesCode.registerLocale(require('i18n-iso-countries/langs/de.json'));

export default {
  name: 'UserSettingsAccount',
  components: {
    ErrorCard,
    ChangePassword,
    ChangeEmail,
    ConfirmDialog,
    UserSettingsAccountDirectDebit,
  },
  props: {
    communicationPreferences: Array,
  },
  data() {
    return {
      communicationChannels: cloneDeep(this.communicationPreferences),
      selectedYear: '',
      selectedMonth: '',
      selectedDay: '',
      // ErrorCard variables
      error: false,
      deleteAccountDialog: false,
      errorMessage: '',
      errorToRecallFunction: '',

      // Confirm Dialog Text for account deletion
      confirmDialogTitle: this.$translate('DELETE_ACCOUNT'),
      confirmDialogMsg: this.$translate('DELETE_ACCOUNT_ARE_YOU_SURE'),
      confirmDialogPositiveBtnText: this.$translate('DELETE_ACCOUNT'),
      confirmDialogNegativeBtnText: this.$translate('CANCEL'),

      dob: '',
      date: '',
      dateFormatted: this.formatDate(new Date().toISOString().substr(0, 10)),
      menu2: false,
      profilePicture: null,
      profilePictureString: '',
      rules: [
        (value) =>
          !value ||
          value.size < 2000000 ||
          this.$translate('AVATAR_SIZE_MAX_2MB'),
      ],
      phoneNumberRules: [
        (value) =>
          !value ||
          !!this.validateNumber(value) ||
          this.$translate('INVALID_PHONE_NO'),
      ],
      requiredRules,
      changePasswordDialogActive: false,
      changeEmailDialogActive: false,
      updateUserLoading: false,

      nationalPhoneNo: '',
      selectedCountryCode: '',
      countryCallingCodes: [],
      whatsappAllowed: false,
      disableWhatsappCheckbox: false,
      SMSAllowed: false,
      phoneErrorMessages: [],
      optOutMsg: this.$translate('OPT_OUT_MSG'),
    };
  },
  created() {
    if (this.user.dob) {
      const dobParts = this.user.dob.split('-');
      if (dobParts.length === 3) {
        this.selectedDay = dobParts[0];
        this.selectedMonth = dobParts[1];
        this.selectedYear = dobParts[2];
      }
    }
    this.getCountryCallingCode();
    this.formatPhoneNumber();
    this.isWhatsappMessagingAllowed();
    this.isSMSMessagingAllowed();
  },
  watch: {
    date() {
      this.dateFormatted = this.formatDate(this.date);
    },
    profilePicture(file) {
      console.log('GOT A PICTURE: ', file);

      this.convertPicture(file);
    },
    selectedCountryCode() {
      this.validateNumber();
    },
  },
  computed: {
    years() {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 100 }, (_, i) => currentYear - i).map(String);
    },
    monthOptions() {
      return [
        { text: this.$translate('JANUARY'), value: '01' },
        { text: this.$translate('FEBRUARY'), value: '02' },
        { text: this.$translate('MARCH'), value: '03' },
        { text: this.$translate('APRIL'), value: '04' },
        { text: this.$translate('MAY'), value: '05' },
        { text: this.$translate('JUNE'), value: '06' },
        { text: this.$translate('JULY'), value: '07' },
        { text: this.$translate('AUGUST'), value: '08' },
        { text: this.$translate('SEPTEMBER'), value: '09' },
        { text: this.$translate('OCTOBER'), value: '10' },
        { text: this.$translate('NOVEMBER'), value: '11' },
        { text: this.$translate('DECEMBER'), value: '12' },
      ];
    },
    ...mapGetters({
      user: 'auth/user',
    }),
    partnerGroupUserRelations() {
      return this.$store.getters['auth/partnerGroupUserRelations'];
    },
    partnerGroupId() {
      return this.$store.getters['Account/partnerGroupDetails'].PartnerGroupId;
    },
    DirectDebitExcluded() {
      const excludedMethods = this.$store.getters.getPartnerExcludedPaymentMethods;
      return excludedMethods.some(method => method.PaymentMethod === "DirectDebit");
    },
    computedDateFormatted() {
      return this.formatDate(this.date);
    },
    getCountryCodes() {
      return Object.keys(countriesCode.getAlpha2Codes());
    },
  },
  methods: {
    getDaysInMonth(year, month) {
      const selectedDate = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0).getDate();
      return Array.from({ length: lastDay }, (_, i) => {
        const day = i + 1;
        return day < 10 ? `0${day}` : String(day);
      });
    },
    formatPhoneNumber() {
      if (this.user.phoneNo) {
        //get only National number to display. country code will be shown in the selection
        const phoneNumber = parsePhoneNumberFromString(this.user.phoneNo);
        if (phoneNumber) {
          const nationalNumber = phoneNumber.nationalNumber;
          const countryCallingCode = phoneNumber.countryCallingCode;
          this.nationalPhoneNo = nationalNumber;
          this.selectedCountryCode = `+${countryCallingCode}`;
          console.log('nationalNumber', nationalNumber);
          console.log('selectedCountryCode', countryCallingCode);
        }
      } else {
        this.selectedCountryCode = '+31';
      }
    },
    getCountryCallingCode() {
      const countryCallingData = Object.values(countries);
      this.countryCallingCodes = countryCallingData.map((country) => ({
        name: country.name + ' ' + `(+${country.phone})`,
        code: country.phone,
        callingCode: `+${country.phone}`,
      }));
    },
    validateNumber() {
      if (this.selectedCountryCode && this.nationalPhoneNo) {
        let phoneNumber = this.selectedCountryCode + this.nationalPhoneNo;
        const isValid = isValidPhoneNumber(phoneNumber);
        console.log('isValid ?', isValid, phoneNumber);
        return isValid;
      }
    },
    getCountryName(code, selection) {
      let locale = '';
      switch (Number(this.$store.getters['auth/userLanguage'].LanguageId)) {
        case 13:
          locale = 'en';
          break;
        case 14:
          locale = 'nl';
          break;
        case 17:
          locale = 'de';
          break;
        default:
          locale = 'en';
      }
      // if (!this.addedLocale) {
      //   countries.registerLocale(
      //     require('i18n-iso-countries/langs/' + locale + '.json')
      //   );
      //   this.addedLocale = true;
      // }
      return countriesCode.getName(code, locale, selection);
    },
    checkForProductsInShoppingCart() {
      // if you have products in your shoppingCart show alert dialog before logout
      if (this.$store.getters['shoppingCart/orders'].length !== 0) {
        this.$emit('showLogoutAlert');
      } else {
        this.logOut();
      }
    },
    markPhoneFieldError(enabled) {
      enabled = false;
      this.phoneErrorMessages = [
        this.$translate('PHONE_NUMBER_REQUIRED_FOR_SMS'),
      ];
      this.$refs.phoneNumber.$el
        .querySelector('input')
        .classList.add('error--text');
    },
    clearPhoneError() {
      this.phoneErrorMessages = [];
      this.$refs.phoneNumber.$el
        .querySelector('input')
        .classList.remove('error--text');
    },
    handleSMSChange(communicationChannel, enabled) {
      if (communicationChannel === 'SMS') {
        if (!this.nationalPhoneNo || this.userClone.nationalPhoneNo === '') {
          enabled = true;
          this.$nextTick(() => {
            this.$refs.postalcode.$el.scrollIntoView({ behavior: 'smooth' });
            this.markPhoneFieldError(enabled);
          });
        } else {
          enabled = !enabled;
        }
      } else {
        enabled = !enabled;
      }
    },
    changeCommunicationPreference(communicationChannel, communication) {
      if (communicationChannel === 'SMS') {
        if (!this.nationalPhoneNo || this.nationalPhoneNo === '') {
          communication.Enabled = false;
          this.$nextTick(() => {
            this.$refs.postalcode.$el.scrollIntoView({ behavior: 'smooth' });
            this.markPhoneFieldError(communication);
          });
        } else {
          communication.Enabled = !communication.Enabled;
        }
      } else {
        communication.Enabled = !communication.Enabled;
        communicationChannel.Enabled = !communicationChannel.Enabled;
        const payload = {
          CommunicationChannel: communicationChannel.CommunicationChannel,
          Enabled: communicationChannel.Enabled,
          CommunicationType: 'ShoppingListReminder',
        };
        doRequest(
          (response) => {
            window.EventBus.fire(
              'activateSnackBar',
              this.$translate('CHANGE_COMMUNICATION_PREFERENCE_SUCCESS')
            );
            this.communicationChannels =
              response.data.CommunicationPreferences[0].CommunicationTypes[0].CommunicationChannels;
          },
          () => {},
          'changeCommunicationPreference',
          this.$store.getters['auth/userName'],
          payload
        );
      }
    },
    hasApp(communicationChannel) {
      if (communicationChannel !== 'App') {
        return true;
      }
      if (
        this.partnerGroupUserRelations.DeviceToken &&
        this.partnerGroupUserRelations.length > 0
      ) {
        return true;
      }
      return false;
    },
    logOut() {
      UserObject.logout();
      window.EventBus.fire(
        'activateSnackBar',
        this.$translate('SUCCESS_LOGGED_OUT')
      );

      this.$router.push({
        name: 'Homepage',
        params: {
          account_id: this.$store.getters.getRegion,
        },
      });
    },
    formatDate() {
      if (this.selectedDay && this.selectedMonth && this.selectedYear) {
        const formattedDay = this.selectedDay.toString().padStart(2, '0');
        const formattedMonth = this.selectedMonth;
        const formattedYear = this.selectedYear;
        this.user.dob = `${formattedDay}-${formattedMonth}-${formattedYear}`;
      }
    },
    deleteUser() {
      this.error = false;

      const payload = {
        Id: this.$store.getters['auth/userName'],
      };
      doRequest(
        () => {
          this.logOut();
        },
        () => {
          this.error = true;
          this.errorMessage = this.$translate('ERROR_DELETE_USER');
          this.errorToRecallFunction = 'deleteUser';
        },
        'deleteUser',
        `${this.$store.getters['Account/partnerGroupDetails'].AccountId}_UM`,
        payload
      );
    },
    async updateUser() {
      if (!this.$refs.userForm.validate()) {
        window.EventBus.fire(
          'activateSnackBar',
          this.$translate('LINDA_FILL_REQUIRED')
        );
        return;
      }

      this.error = false;

      if (this.nationalPhoneNo != '') {
        this.user.phoneNo = this.selectedCountryCode + this.nationalPhoneNo;
      } else this.user.phoneNo = '';

      const payload = {
        email: this.user.email,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        dob: `${this.selectedDay}-${this.selectedMonth}-${this.selectedYear}`,
        city: this.user.city,
        postalCode: this.user.postalCode,
        street: this.user.street,
        houseNo: this.user.houseNo,
        country: this.user.country,
        countryCode: this.user.countryCode,
        phoneNo: this.user.phoneNo,
        picture: this.user.picture,
        iban: this.user.iban,
        accountHolderName: this.user.accountHolderName,
        allowDirectDebit: this.user.allowDirectDebit,
        longitude: this.user.longitude,
        latitude: this.user.latitude,
        professionalUser: this.user.professionalUser,
        companyName: this.user.companyName,
        kvkNumber: this.user.kvkNumber,
        vatNumber: this.user.vatNumber,
      };
      payload.FunctionId = 'UserFunctionLibrary::updateUser';
      this.updateUserLoading = true;
      try {
        await UserObject.updateUser(payload);
        this.updateUserLoading = false;
        this.user.dob = `${this.selectedDay}-${this.selectedMonth}-${this.selectedYear}`;
        console.log(this.user.dob);
        this.$store.commit('auth/setUser', this.user);
        window.EventBus.fire('getUserData');
      } catch (error) {
        this.updateUserLoading = false;
        this.error = true;
        this.errorMessage = this.$translate('FAILED_SAVING_SETTINGS');
        this.errorToRecallFunction = 'updateUser';
      }
    },
    getUserAddress() {
      return {
        Street: this.user.street,
        Nr: this.user.houseNo,
        PostalCode: this.user.postalCode,
        City: this.user.city,
        CountryCode: this.user.countryCode,
        Latitude: this.user.latitude == null ? -200 : this.user.latitude,
        Longitude: this.user.longitude == null ? -200 : this.user.longitude,
      };
    },
    isAddressComplete() {
      if (this.user.houseNo == null || this.user.houseNo.trim() === '') {
        return false;
      }
      if (this.user.street == null || this.user.street.trim() === '') {
        return false;
      }
      if (this.user.postalCode == null || this.user.postalCode.trim() === '') {
        return false;
      }
      if (this.user.city == null || this.user.city.trim() === '') {
        return false;
      }
      return true;
    },
    geocodeAddress() {
      // Get gps location based on user's address info
      if (this.user.latitude == null) {
        this.user.latitude = -200;
      }
      if (this.user.longitude == null) {
        this.user.longitude = -200;
      }

      if (!this.isAddressComplete()) {
        return;
      }

      // Start geocoding
      const userAddressObj = this.getUserAddress();
      forwardGeocoding(
        userAddressObj,
        (result) => {
          // Geocoding finished successfully
          if (result.Latitude != null) {
            this.user.latitude = result.Latitude;
          }
          if (result.Longitude != null) {
            this.user.longitude = result.Longitude;
          }
        },
        () => {
          // Geocoding finished with error
        }
      );
    },
    onChangePasswordBtnClick() {
      this.changePasswordDialogActive = true;
    },
    onChangeEmailBtnClick() {
      this.changeEmailDialogActive = true;
    },
    async requestWhatsappOptIn() {
      try {
        let response = await UserObject.requestWhatsappOptIn();
        UserObject.parsePartnerGroupUserRelations(response.data);
        this.isWhatsappMessagingAllowed();
      } catch (error) {
        console.log('ERROR', error);
      }
    },
    async whatsappOptOut() {
      try {
        let response = await UserObject.whatsappOptOut();
        UserObject.parsePartnerGroupUserRelations(response.data);
        this.isWhatsappMessagingAllowed();
      } catch (error) {
        console.log('ERROR', error);
      }
    },
    isWhatsappMessagingAllowed() {
      if (this.partnerGroupUserRelations !== null) {
        this.partnerGroupUserRelations.forEach((relation) => {
          if (relation.PartnerGroupId === this.partnerGroupId)
            this.whatsappAllowed = relation.WhatsappMessagingAllowed;
        });
      }
    },
    async requestSMSOptIn() {
      try {
        let response = await UserObject.requestSMSOptIn();
        UserObject.parsePartnerGroupUserRelations(response.data);
        this.isSMSMessagingAllowed();
      } catch (error) {
        console.log('ERROR', error);
      }
    },
    async SMSOptOut() {
      try {
        let response = await UserObject.SMSOptOut();
        UserObject.parsePartnerGroupUserRelations(response.data);
        this.isSMSMessagingAllowed();
      } catch (error) {
        console.log('ERROR', error);
      }
    },
    isSMSMessagingAllowed() {
      if (this.partnerGroupUserRelations !== null) {
        this.partnerGroupUserRelations.forEach((relation) => {
          if (relation.PartnerGroupId === this.partnerGroupId) {
            this.SMSAllowed = relation.SMSMessagingAllowed;
          }
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
a {
  text-decoration: inherit;
  color: inherit;
  cursor: pointer;
}
.error--text {
  border-color: red !important;
  color: red;
}
</style>
